import React from 'react';
import { Box } from '@mui/material';

import { AngleField, DistanceField } from '@paragon/material';

import { updateShapeDetailsAndRegenerateTruss } from '../../../../../../store/actions/insertComponentDesignActions';
import { AtticShapeDetails as AtticShapeDetailsInterface } from '../../../../../../store/slices/applicationSlice';
import { useAppDispatch } from '../../../../../hooks';
import { AtticSettings } from '../../../properties/AtticSettings';
import { LabeledBox } from '../../../properties/LabeledBox';
import { OverhangSettings } from '../../../properties/OverhangSettings';
import { createExceedsMaxLengthErrorAction, exceedsMaxLength, minTrussLength } from './Length';
import { ShapeDetailsHeelSettings } from './ShapeDetailsHeelSettings';

const textFieldSx = { m: 1, flex: 1 } as const;

interface Props {
  readonly shapeDetails: AtticShapeDetailsInterface;
}

export function AtticShapeDetails({ shapeDetails }: Props) {
  const dispatch = useAppDispatch();

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <DistanceField
          label="Length"
          value={shapeDetails.span}
          min={minTrussLength}
          onSubmit={async (value) => {
            if (exceedsMaxLength(value)) {
              dispatch(createExceedsMaxLengthErrorAction());
              return;
            }

            await dispatch(updateShapeDetailsAndRegenerateTruss({ ...shapeDetails, span: value }));
          }}
          sx={textFieldSx}
        />
        <AngleField
          label="Slope"
          sx={textFieldSx}
          value={shapeDetails.slopeInDegrees}
          defaultDisplayFormat="slope"
          onSubmit={async (value) => {
            await dispatch(updateShapeDetailsAndRegenerateTruss({ ...shapeDetails, slopeInDegrees: value }));
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
        <LabeledBox title="Heel">
          <ShapeDetailsHeelSettings
            heelSettings={shapeDetails.heelSettings}
            topChordSlopeInDegrees={shapeDetails.slopeInDegrees}
            onChange={async (value) => {
              await dispatch(updateShapeDetailsAndRegenerateTruss({ ...shapeDetails, heelSettings: value }));
            }}
          />
        </LabeledBox>

        <LabeledBox title="Overhang">
          <OverhangSettings
            overhangSettings={shapeDetails.overhang}
            onChange={async (value) => {
              await dispatch(updateShapeDetailsAndRegenerateTruss({ ...shapeDetails, overhang: value }));
            }}
          />
        </LabeledBox>
      </Box>

      <LabeledBox title="Attic Settings (Beta)">
        <AtticSettings
          settings={shapeDetails.atticSettings}
          disallowNull
          span={shapeDetails.span}
          centerOnPeakButton={false}
          onChange={async (atticSettings) => {
            await dispatch(updateShapeDetailsAndRegenerateTruss({ ...shapeDetails, atticSettings: atticSettings! }));
          }}
        />
      </LabeledBox>
    </>
  );
}
